/** Build line-based editable blocks from pdf.js text items (easier than per-glyph clicks). */

import { buildFontMetaForItem } from "helpers/pdfFontMapping";

const PDF_Y_TOLERANCE = 2.5;
const PDF_X_GAP_FACTOR = 1.25;

const fontMetaFromItem = (page, item, styles) => buildFontMetaForItem(page, item, styles);

export const buildEditableTextBlocks = (rawItems = [], pageNumber = 1, page = null, styles = {}) => {
    const items = (rawItems || [])
        .map((item, index) => {
            const text = String(item?.str ?? item?.unicode ?? "");
            if (!text.trim()) return null;

            const fontSize =
                Math.abs(Number(item?.transform?.[3])) ||
                Math.abs(Number(item?.transform?.[0])) ||
                Number(item?.height) ||
                12;
            const pdfX = Number(item?.transform?.[4]) || 0;
            const pdfY = Number(item?.transform?.[5]) || 0;
            const pdfWidth =
                Number(item?.width) || Math.max(fontSize * 0.45 * text.length, fontSize);

            const fontMeta = page ? fontMetaFromItem(page, item, styles) : {
                pdfFontKey: "Helvetica",
                cssFontFamily: 'Helvetica, Arial, sans-serif',
                cssFontWeight: "400",
                cssFontStyle: "normal",
                originalFontName: "",
            };

            return {
                id: `p${pageNumber}-i${index}`,
                page: pageNumber,
                originalText: text,
                fontSize,
                pdfX,
                pdfY,
                pdfWidth,
                pdfHeight: Number(item?.height) || fontSize * 1.15,
                ...fontMeta,
            };
        })
        .filter(Boolean)
        .sort((left, right) => right.pdfY - left.pdfY || left.pdfX - right.pdfX);

    const blocks = [];

    items.forEach((item) => {
        const previous = blocks[blocks.length - 1];
        const sameLine =
            previous &&
            Math.abs(previous.pdfY - item.pdfY) <= PDF_Y_TOLERANCE &&
            item.pdfX - (previous.pdfX + previous.pdfWidth) <=
                previous.fontSize * PDF_X_GAP_FACTOR;

        if (sameLine) {
            const gap = item.pdfX - (previous.pdfX + previous.pdfWidth);
            const overlaps = gap < previous.fontSize * 0.35;
            if (!overlaps) {
            previous.originalText += item.originalText;
            previous.pdfWidth = item.pdfX + item.pdfWidth - previous.pdfX;
            previous.pdfHeight = Math.max(previous.pdfHeight, item.pdfHeight);
            previous.sourceIds.push(item.id);
            return;
            }
        }

        blocks.push({
            ...item,
            sourceIds: [item.id],
        });
    });

    return blocks.map((block) => ({
        ...block,
        extractedText: block.originalText,
    }));
};

export const isErasedEdit = (edit) =>
    Boolean(
        edit &&
            !edit.isInsert &&
            edit.newText !== undefined &&
            edit.newText !== null &&
            !String(edit.newText).trim()
    );

export const resolveBlockDisplayText = (block, edits = {}) => {
    const edit = edits[block?.id];
    if (isErasedEdit(edit)) {
        return "";
    }
    if (edit && edit.newText !== undefined && edit.newText !== null) {
        return String(edit.newText);
    }
    return String(block?.extractedText ?? block?.originalText ?? "");
};

export const blocksOnSamePdfTextRun = (left, right) => {
    if (!left || !right || left.page !== right.page) return false;
    if (Math.abs(left.pdfY - right.pdfY) > PDF_Y_TOLERANCE * 2) return false;
    const leftRight = left.pdfX + (left.pdfWidth || 0);
    const rightRight = right.pdfX + (right.pdfWidth || 0);
    const gap =
        Math.max(left.pdfX, right.pdfX) - Math.min(leftRight, rightRight);
    return gap <= Math.max(left.fontSize, right.fontSize) * PDF_X_GAP_FACTOR;
};

export const collectPdfTextRunBlocks = (primary, blocks = []) => {
    const onPage = (blocks || []).filter(
        (item) => item.page === primary.page && !item.isCustom
    );
    const run = new Set([primary]);
    let expanded = true;
    while (expanded) {
        expanded = false;
        onPage.forEach((candidate) => {
            if (run.has(candidate)) return;
            for (const member of run) {
                if (blocksOnSamePdfTextRun(candidate, member)) {
                    run.add(candidate);
                    expanded = true;
                    break;
                }
            }
        });
    }
    return Array.from(run);
};

export const expandEraseEditEntry = (primary, allBlocks, entry) => {
    if (!entry || entry.isInsert || String(entry.newText ?? "").trim()) {
        return entry;
    }
    const run = collectPdfTextRunBlocks(primary, allBlocks);
    if (run.length <= 1) return entry;

    let minX = entry.x ?? primary.pdfX;
    let maxRight = minX + (entry.width ?? primary.pdfWidth ?? 0);
    let sourceText = String(entry.originalText ?? "");
    run.forEach((block) => {
        minX = Math.min(minX, block.pdfX);
        maxRight = Math.max(maxRight, block.pdfX + (block.pdfWidth || 0));
        const chunk = String(block.extractedText ?? block.originalText ?? "");
        if (chunk.length > sourceText.length) {
            sourceText = chunk;
        }
    });

    return {
        ...entry,
        originalText: sourceText || entry.originalText,
        x: minX,
        width: Math.max(maxRight - minX, entry.width ?? primary.pdfWidth ?? 0),
    };
};

const ERASE_MATCH_PAD_X = 4;
const ERASE_MATCH_PAD_Y = 3;

const replacementCoversPdfBlock = (block, replacement) => {
    if (!block || !replacement || block.page !== replacement.page) return false;
    if (Math.abs(block.pdfY - Number(replacement.y)) > PDF_Y_TOLERANCE * 4) {
        return false;
    }
    const fontSize = Math.max(6, Number(replacement.fontSize) || Number(block.fontSize) || 12);
    const replLeft = Number(replacement.x) - ERASE_MATCH_PAD_X;
    const replRight =
        Number(replacement.x) +
        Math.max(Number(replacement.width) || 0, fontSize * 0.35) +
        ERASE_MATCH_PAD_X;
    const blockLeft = block.pdfX;
    const blockRight = block.pdfX + (block.pdfWidth || 0);
    const horizontal =
        Math.max(blockLeft, replLeft) < Math.min(blockRight, replRight) - 0.25;
    if (!horizontal) return false;

    const blockTop = block.pdfY + fontSize * 0.35;
    const blockBottom = block.pdfY - fontSize * ERASE_MATCH_PAD_Y;
    const replTop = Number(replacement.y) + fontSize * 0.35;
    const replBottom = Number(replacement.y) - fontSize * (0.78 + 0.22) - ERASE_MATCH_PAD_Y;
    return Math.max(blockBottom, replBottom) < Math.min(blockTop, replTop);
};

const editEntryFromErasureReplacement = (block, replacement) => ({
    page: block.page,
    originalText: String(
        replacement.originalText ?? block.extractedText ?? block.originalText ?? ""
    ),
    newText: "",
    isInsert: false,
    x: block.pdfX,
    y: block.pdfY,
    width: block.pdfWidth,
    height: block.pdfHeight,
    fontSize: replacement.fontSize ?? block.fontSize,
    pdfFontKey: replacement.pdfFontKey ?? block.pdfFontKey ?? "Helvetica",
    cssFontFamily: replacement.cssFontFamily ?? block.cssFontFamily,
    cssFontWeight: replacement.cssFontWeight ?? block.cssFontWeight,
    cssFontStyle: replacement.cssFontStyle ?? block.cssFontStyle,
    textColorR: replacement.textColorR ?? block.textColor?.r ?? 0,
    textColorG: replacement.textColorG ?? block.textColor?.g ?? 0,
    textColorB: replacement.textColorB ?? block.textColor?.b ?? 0,
    fillColorR: block.fillColor?.r ?? 1,
    fillColorG: block.fillColor?.g ?? 1,
    fillColorB: block.fillColor?.b ?? 1,
    styleChanged: false,
});

export const blockIsHiddenByErasureZone = (block, zones = []) =>
    (zones || []).some((zone) => {
        if (!block || !zone || block.page !== zone.page) return false;
        if ((zone.hiddenBlockIds || []).includes(block.id)) return true;
        const fontSize = Math.max(
            8,
            Number(zone.fontSize) || Number(block.fontSize) || 12
        );
        if (Math.abs(block.pdfY - Number(zone.y)) > fontSize * 1.6) return false;
        const zoneLeft = Number(zone.x) - fontSize * 0.5;
        const zoneRight =
            Number(zone.x) + Math.max(Number(zone.width) || 0, fontSize) + fontSize * 0.5;
        const blockLeft = block.pdfX;
        const blockRight = block.pdfX + (block.pdfWidth || 0);
        return Math.max(blockLeft, zoneLeft) < Math.min(blockRight, zoneRight) - 0.25;
    });

const erasureZoneKey = (zone) =>
    `${zone.page}:${zone.x}:${zone.y}:${zone.width}:${zone.height}`;

export const hiddenPdfBlockIdsForErase = (primary, allBlocks = []) => {
    const ids = new Set();
    collectPdfTextRunBlocks(primary, allBlocks).forEach((item) => {
        if (item?.id && !item.isCustom) ids.add(item.id);
    });
    if (primary?.id && !primary.isCustom) ids.add(primary.id);
    return Array.from(ids);
};

export const erasureZoneFromEditEntry = (entry, block, extraHiddenIds = []) => ({
    id: `zone-${block.id}-${Date.now()}`,
    page: entry.page ?? block.page,
    x: entry.x ?? block.pdfX,
    y: entry.y ?? block.pdfY,
    width: entry.width ?? block.pdfWidth,
    height: entry.height ?? block.pdfHeight,
    hiddenBlockIds: extraHiddenIds,
    originalText: String(entry.originalText ?? ""),
    fontSize: entry.fontSize ?? block.fontSize,
    pdfFontKey: entry.pdfFontKey ?? block.pdfFontKey ?? "Helvetica",
    cssFontFamily: entry.cssFontFamily ?? block.cssFontFamily,
    cssFontWeight: entry.cssFontWeight ?? block.cssFontWeight,
    cssFontStyle: entry.cssFontStyle ?? block.cssFontStyle,
    textColorR: entry.textColorR ?? block.textColor?.r ?? 0,
    textColorG: entry.textColorG ?? block.textColor?.g ?? 0,
    textColorB: entry.textColorB ?? block.textColor?.b ?? 0,
    fillColorR: entry.fillColorR ?? block.fillColor?.r ?? 1,
    fillColorG: entry.fillColorG ?? block.fillColor?.g ?? 1,
    fillColorB: entry.fillColorB ?? block.fillColor?.b ?? 1,
});

export const blankCustomBlockFromErasureZone = (zone, freshId) => ({
    id: freshId,
    isCustom: true,
    isBlankSlot: true,
    erasureZoneId: zone.id,
    page: zone.page,
    pdfX: zone.x,
    pdfY: zone.y,
    pdfWidth: zone.width,
    pdfHeight: zone.height,
    fontSize: zone.fontSize,
    pdfFontKey: zone.pdfFontKey,
    cssFontFamily: zone.cssFontFamily,
    cssFontWeight: zone.cssFontWeight,
    cssFontStyle: zone.cssFontStyle,
    textColor: { r: zone.textColorR, g: zone.textColorG, b: zone.textColorB },
    fillColor: { r: zone.fillColorR, g: zone.fillColorG, b: zone.fillColorB },
});

/** After save reload: blank slots + zones (no edit history on PDF blocks). */
export const buildBlankSlotsFromErasureReplacements = (blocks = [], replacements = []) => {
    const erasures = (replacements || []).filter(
        (item) => !item?.isInsert && isErasedEdit(item)
    );
    if (!erasures.length) {
        return { zones: [], blankBlocks: [] };
    }

    const zones = [];
    const blankBlocks = [];
    const seen = new Set();

    erasures.forEach((replacement, index) => {
        const zone = {
            id: `zone-reload-${replacement.page}-${replacement.x}-${replacement.y}-${index}`,
            page: replacement.page,
            x: replacement.x,
            y: replacement.y,
            width: replacement.width,
            height: replacement.height,
            originalText: String(replacement.originalText ?? ""),
            fontSize: replacement.fontSize,
            pdfFontKey: replacement.pdfFontKey,
            cssFontFamily: replacement.cssFontFamily,
            cssFontWeight: replacement.cssFontWeight,
            cssFontStyle: replacement.cssFontStyle,
            textColorR: replacement.textColorR,
            textColorG: replacement.textColorG,
            textColorB: replacement.textColorB,
            fillColorR: replacement.fillColorR ?? 1,
            fillColorG: replacement.fillColorG ?? 1,
            fillColorB: replacement.fillColorB ?? 1,
        };
        const key = erasureZoneKey(zone);
        if (seen.has(key)) return;
        seen.add(key);

        const matches = blocks.filter((block) =>
            replacementCoversPdfBlock(block, replacement)
        );
        zone.hiddenBlockIds = matches.flatMap((block) =>
            hiddenPdfBlockIdsForErase(block, blocks)
        );
        const freshId = `blank-reload-${key}`;
        zones.push(zone);
        blankBlocks.push(blankCustomBlockFromErasureZone(zone, freshId));
    });

    return { zones, blankBlocks };
};

/** Re-apply erase state after save reload (pdf.js still extracts text under white rects). */
export const buildErasureEditsFromReplacements = (blocks = [], replacements = []) => {
    const erasures = (replacements || []).filter(
        (item) => !item?.isInsert && isErasedEdit(item)
    );
    if (!erasures.length || !blocks?.length) return {};

    const edits = {};
    erasures.forEach((replacement) => {
        const matches = blocks.filter((block) =>
            replacementCoversPdfBlock(block, replacement)
        );
        const runMembers = new Set();
        matches.forEach((block) => {
            collectPdfTextRunBlocks(block, blocks).forEach((member) =>
                runMembers.add(member)
            );
        });
        runMembers.forEach((block) => {
            if (edits[block.id]) return;
            edits[block.id] = editEntryFromErasureReplacement(block, replacement);
        });
    });
    return edits;
};

const blocksOverlapOnPage = (left, right, yTolerance) => {
    if (Math.abs(left.pdfY - right.pdfY) > yTolerance) return false;
    const leftRight = left.pdfX + (left.pdfWidth || 0);
    const rightRight = right.pdfX + (right.pdfWidth || 0);
    const gap = Math.max(left.pdfX, right.pdfX) - Math.min(leftRight, rightRight);
    return gap <= Math.max(left.fontSize, right.fontSize) * PDF_X_GAP_FACTOR;
};

const pdfHorizontalRangesOverlap = (left, right) => {
    const leftRight = left.pdfX + (left.pdfWidth || 0);
    const rightRight = right.pdfX + (right.pdfWidth || 0);
    return Math.max(left.pdfX, right.pdfX) < Math.min(leftRight, rightRight) - 0.25;
};

const isDuplicatePdfShard = (block, anchor) => {
    if (!blocksOverlapOnPage(block, anchor, PDF_Y_TOLERANCE * 2)) return false;
    const blockText = String(block.extractedText ?? block.originalText ?? "").trim();
    const anchorExtract = String(
        anchor._shardSourceText ?? anchor.extractedText ?? ""
    ).trim();
    const anchorDisplay = String(anchor.originalText ?? "").trim();
    if (!blockText || !anchorExtract) return false;
    if (blockText === anchorExtract || blockText === anchorDisplay) return true;
    if (anchorExtract.includes(blockText) && blockText.length < anchorExtract.length) {
        return true;
    }
    if (blockText.includes(anchorExtract) && anchorExtract.length < blockText.length) {
        return true;
    }
    return false;
};

export const buildEditableBlocksFromWidgetAnnotations = (annotations = [], pageNumber = 1) => {
    return (annotations || [])
        .filter((annotation) => {
            const rect = annotation?.rect;
            if (!rect || rect.length < 4) return false;
            if (annotation.subtype === "Widget") return true;
            if (annotation.subtype === "FreeText") return true;
            if (annotation.subtype === "Text" && annotation.contents) return true;
            return false;
        })
        .map((annotation, index) => {
            const rect = annotation.rect || [0, 0, 0, 0];
            const left = Math.min(rect[0], rect[2]);
            const right = Math.max(rect[0], rect[2]);
            const bottom = Math.min(rect[1], rect[3]);
            const top = Math.max(rect[1], rect[3]);
            const width = Math.max(right - left, 12);
            const height = Math.max(top - bottom, 10);
            const text = String(
                annotation.fieldValue ??
                    annotation.value ??
                    annotation.defaultFieldValue ??
                    annotation.contents ??
                    annotation.title ??
                    ""
            );
            const fontSize = Math.max(8, Math.min(24, height * 0.72));
            const fieldName = String(annotation.fieldName || annotation.title || index);

            return {
                id: `p${pageNumber}-form-${fieldName.replace(/\W+/g, "_")}-${index}`,
                page: pageNumber,
                originalText: text,
                extractedText: text,
                fontSize,
                pdfX: left,
                pdfY: top,
                pdfWidth: width,
                pdfHeight: height,
                pdfFontKey: "Helvetica",
                cssFontFamily: "Helvetica, Arial, sans-serif",
                cssFontWeight: "400",
                cssFontStyle: "normal",
                fontName: fieldName,
                originalFontName: "",
                sourceIds: [`form-${fieldName}`],
                isFormField: true,
            };
        });
};

export const dedupeOverlappingExtractedBlocks = (blocks = []) => {
    if (!blocks?.length || blocks.length <= 1) return blocks || [];
    const sorted = [...blocks].sort(
        (a, b) =>
            String(b.extractedText ?? b.originalText ?? "").trim().length -
            String(a.extractedText ?? a.originalText ?? "").trim().length
    );
    const kept = [];
    sorted.forEach((block) => {
        const duplicate = kept.some((anchor) => {
            if (anchor.page !== block.page) return false;
            if (!blocksOverlapOnPage(block, anchor, PDF_Y_TOLERANCE * 2)) return false;
            const blockText = String(
                block.extractedText ?? block.originalText ?? ""
            ).trim();
            const anchorText = String(
                anchor.extractedText ?? anchor.originalText ?? ""
            ).trim();
            if (!blockText || !anchorText) return false;
            if (blockText === anchorText) return true;
            return (
                isDuplicatePdfShard(block, anchor) ||
                isDuplicatePdfShard(anchor, block)
            );
        });
        if (!duplicate) kept.push(block);
    });
    return kept;
};

export const mergeExtractedAndFormBlocks = (textBlocks = [], formBlocks = []) => {
    if (!formBlocks.length) return textBlocks;
    if (!textBlocks.length) return formBlocks;
    const merged = [...textBlocks];
    formBlocks.forEach((formBlock) => {
        const duplicate = merged.some(
            (block) =>
                Math.abs(block.pdfY - formBlock.pdfY) <= PDF_Y_TOLERANCE * 2 &&
                Math.abs(block.pdfX - formBlock.pdfX) <= formBlock.fontSize * 2 &&
                String(block.originalText).trim() === String(formBlock.originalText).trim()
        );
        if (!duplicate) merged.push(formBlock);
    });
    return merged;
};

export const mergeTextBlocksForEditor = (
    blocks = [],
    edits = {},
    pageNumber = 1,
    erasureZones = []
) => {
    const hiddenIds = new Set(
        (erasureZones || []).flatMap((zone) => zone.hiddenBlockIds || [])
    );
    const onPage = blocks.filter(
        (block) =>
            block.page === pageNumber &&
            !hiddenIds.has(block.id) &&
            !blockIsHiddenByErasureZone(block, erasureZones)
    );
    const withDisplay = onPage.map((block) => {
        const edit = edits[block.id];
        const displayText = resolveBlockDisplayText(block, edits);
        const erased = isErasedEdit(edit);
        const hasAppliedEdit = Boolean(
            edit && edit.newText !== undefined && edit.newText !== null
        );
        const shardSource = String(
            edit?.originalText ?? block.extractedText ?? block.originalText ?? ""
        );
        return {
            ...block,
            extractedText: erased ? "" : block.extractedText ?? block.originalText,
            originalText: displayText,
            _hasAppliedEdit: hasAppliedEdit,
            _shardSourceText: hasAppliedEdit ? shardSource : undefined,
        };
    });

    const editedAnchors = withDisplay.filter((block) => block._hasAppliedEdit);
    if (!editedAnchors.length) {
        return withDisplay.map(({ _hasAppliedEdit, ...block }) => block);
    }

    return withDisplay
        .filter((block) => {
            if (block._hasAppliedEdit || edits[block.id]) return true;
            for (const anchor of editedAnchors) {
                if (anchor.id === block.id) continue;
                if (isDuplicatePdfShard(block, anchor)) return false;
                if (isErasedEdit(edits[anchor.id])) {
                    if (blocksOnSamePdfTextRun(block, anchor)) return false;
                }
                if (Math.abs(block.pdfY - anchor.pdfY) <= PDF_Y_TOLERANCE * 2) {
                    if (pdfHorizontalRangesOverlap(block, anchor)) {
                        return false;
                    }
                }
            }
            return true;
        })
        .map(({ _hasAppliedEdit, ...block }) => block);
};
